import React, { useState, useEffect } from 'react';
import { useGameContext } from '../../context/GameContext';
import { getSocket } from '../../api';
import styles from '../../styles/AdminLockManager.module.css';
import adminApi from '../../adminApi';

const AdminLockManager = () => {
  const [activeTab, setActiveTab] = useState('clientCache');
  const [locks, setLocks] = useState([]);
  const { locksCache, setLocksCache } = useGameContext();
  const socket = getSocket();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [auditInProgress, setAuditInProgress] = useState(false);

  // Get unique weeks from locks
  const availableWeeks = [...new Set(locks.map(lock => lock.weekNumber))].sort((a, b) => a - b);

  useEffect(() => {
    console.log('Fetching locks for tab:', activeTab);
    setIsLoading(true);
    if (activeTab === 'clientCache') {
      if (locksCache && typeof locksCache === 'object') {
        const flattenedLocks = Object.values(locksCache).flat();
        const normalizedLocks = flattenedLocks.map(normalizeLockData);
        console.log('Normalized client cache locks:', normalizedLocks);
        setLocks(normalizedLocks);
      } else {
        setLocks([]);
      }
      setIsLoading(false);
    } else if (activeTab === 'database') {
      socket.emit('admin_fetch_locks', { source: activeTab, week: selectedWeek });
    }
  }, [activeTab, locksCache, socket, selectedWeek]);

  const normalizeLockData = (lock) => ({
    id: lock.id,
    userId: lock.userId || lock.user_id,
    gameId: lock.gameId || lock.game_id,
    pickType: lock.pickType || lock.pick_type,
    weekNumber: lock.weekNumber || lock.week_number,
    lockLine: lock.lockLine || lock.lock_line,
    teamId: lock.team,
    pickLeadTime: lock.pickLeadTime || lock.pick_lead_time,
    createdAt: lock.createdAt || lock.created_at,
    status: lock.status,
    margin: lock.margin,
    wonLost: lock.wonLost || lock.won_lost
  });

  useEffect(() => {
    socket.on('admin_locks_fetched', (data) => {
      console.log('Received admin_locks_fetched event with data:', data);
      if (Array.isArray(data.locks)) {
        setLocks(data.locks.map(normalizeLockData));
      } else if (typeof data.locks === 'object' && data.locks !== null) {
        const flattenedLocks = Object.values(data.locks).flat();
        setLocks(flattenedLocks.map(normalizeLockData));
      } else {
        console.error('Received locks data is not an array or object:', data.locks);
        setLocks([]);
      }
      setIsLoading(false);
    });

    socket.on('audit_completed', (result) => {
      setAuditInProgress(false);
      alert(`Audit completed. ${result.correctedUsers || 0} users had stats corrected.`);
    });

    return () => {
      socket.off('admin_locks_fetched');
      socket.off('audit_completed');
    };
  }, [socket]);

  const handlePurge = (lockId, source) => {
    if (source === 'clientCache') {
      setLocksCache(prevCache => {
        const newCache = { ...prevCache };
        Object.keys(newCache).forEach(weekNumber => {
          newCache[weekNumber] = newCache[weekNumber].filter(lock => lock.id !== lockId);
        });
        return newCache;
      });
    } else {
      socket.emit('admin_purge_lock', { lockId, source });
    }
    setLocks(prevLocks => prevLocks.filter(lock => lock.id !== lockId));
  };

  const handlePurgeAll = (source) => {
    if (source === 'clientCache') {
      setLocksCache({});
    } else {
      socket.emit('admin_purge_all_locks', { source });
    }
    setLocks([]);
  };

  const triggerAudit = async () => {
    setAuditInProgress(true);
    try {
      const response = await adminApi.post('/admin/trigger-audit');
      alert(`Audit completed. ${response.data.correctedUsers || 0} users had stats corrected.`);
    } catch (error) {
      console.error('Error triggering audit:', error);
      alert('Failed to complete audit. Check console for details.');
    } finally {
      setAuditInProgress(false);
    }
  };

  const filteredLocks = locks.filter(lock => lock.weekNumber === selectedWeek);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Lock Management</h2>
        <div className={styles.controls}>
          <div className={styles.tabs}>
            <button 
              className={`${styles.button} ${activeTab === 'clientCache' ? styles.active : ''}`}
              onClick={() => setActiveTab('clientCache')}
            >
              Server Cache
            </button>
            <button 
              className={`${styles.button} ${activeTab === 'database' ? styles.active : ''}`}
              onClick={() => setActiveTab('database')}
            >
              Database
            </button>
          </div>
          <select
            className={styles.select}
            value={selectedWeek}
            onChange={(e) => setSelectedWeek(Number(e.target.value))}
          >
            {availableWeeks.map(week => (
              <option key={week} value={week}>Week {week}</option>
            ))}
          </select>
          <button 
            className={`${styles.button} ${styles.audit}`}
            onClick={triggerAudit}
            disabled={auditInProgress}
          >
            {auditInProgress ? 'Audit in Progress...' : 'Trigger User Stats Audit'}
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className={styles.loading}>Loading locks...</div>
      ) : filteredLocks.length === 0 ? (
        <div className={styles.noData}>No locks found for Week {selectedWeek}.</div>
      ) : (
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Lock ID</th>
                <th>User ID</th>
                <th>Game ID</th>
                <th>Pick Type</th>
                <th>Lock Line</th>
                <th>Team ID</th>
                <th>Pick Lead Time</th>
                <th>Date/Time Locked</th>
                <th>Status</th>
                <th>Margin</th>
                <th>Won/Lost</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLocks.map((lock, index) => {
                const lockId = lock.id || `unknown-${index}`;
                return (
                  <tr key={`${lockId}_${lock.userId}_${lock.gameId}`}>
                    <td>{lockId}</td>
                    <td>{lock.userId || 'N/A'}</td>
                    <td>{lock.gameId || 'N/A'}</td>
                    <td>{lock.pickType || 'N/A'}</td>
                    <td>{lock.lockLine || 'N/A'}</td>
                    <td>{lock.teamId || 'N/A'}</td>
                    <td>{lock.pickLeadTime || 'N/A'}</td>
                    <td>{lock.createdAt ? new Date(lock.createdAt).toLocaleString() : 'Invalid Date'}</td>
                    <td>{lock.status || 'N/A'}</td>
                    <td>{lock.margin !== null ? lock.margin : 'N/A'}</td>
                    <td>{lock.wonLost !== null ? lock.wonLost : 'N/A'}</td>
                    <td>
                      <button 
                        className={`${styles.button} ${styles.danger}`}
                        onClick={() => handlePurge(lockId, activeTab)}
                      >
                        Purge
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className={styles.footer}>
        <button 
          className={`${styles.button} ${styles.danger}`}
          onClick={() => handlePurgeAll(activeTab)}
        >
          Purge All
        </button>
      </div>
    </div>
  );
};

export default AdminLockManager;

